import { useMemo } from "react";

import {
  useAppProperties,
  useCollections,
  usePlaylists,
  useVideos,
} from "@/hooks";
import { ItemType } from "@/api";
import { Dropdown, ErrorPage, PageLoader } from "@/components";
import { Header } from "@/app/modules/Navigation";
import { useTranslate } from "@/providers/context";

import { cn, getTabIconFilename } from "@/utils";
import { Delete, Ellipsis } from "@/icons";

import { PlaylistGrid } from "./components/PlaylistGrid.tsx";
import { useDeletePlaylist } from "./hooks/useDeletePlaylist.tsx";
import toast from "react-hot-toast";

interface CustomPlaylistProps {
  playlistId: number;
}

export const CustomPlaylist = ({ playlistId }: CustomPlaylistProps) => {
  const { data } = usePlaylists();
  const { translate } = useTranslate();
  const playlist = useMemo(() => {
    return data?.Playlists?.find((p) => p.PlaylistId === playlistId);
  }, [data?.Playlists, playlistId]);
  const deletePlaylist = useDeletePlaylist();
  const { isDarkMode } = useAppProperties();

  const { items, videoIds, collectionIds } = useMemo(() => {
    if (!playlist?.Items) {
      return { items: [], videoIds: [], collectionIds: [] };
    }

    const items: { ChildId: string; Type: ItemType; Position: number }[] = [];
    const videoIds: number[] = [];
    const collectionIds: number[] = [];

    playlist.Items.forEach((item) => {
      if (!item.ItemId) {
        return;
      }
      items.push({
        ChildId: item.ItemId.toString(),
        Type: item.ItemType,
        Position: item.Position,
      });

      if (item.ItemType === "video") {
        videoIds.push(item.ItemId);
      } else if (item.ItemType === "collection") {
        collectionIds.push(item.ItemId);
      }
    });
    items.sort((a, b) => a.Position - b.Position);

    return { items, videoIds, collectionIds };
  }, [playlist?.Items]);
  const { videoById } = useVideos({ ids: videoIds });
  const { collectionById } = useCollections({ ids: collectionIds });

  const noItems = playlist?.Items && playlist?.Items.length === 0;
  const contentNotLoaded = !videoById || !collectionById;
  if (!data || (contentNotLoaded && !noItems)) {
    return <PageLoader />;
  }

  if (!playlist) {
    return <ErrorPage code={404} />;
  }

  return (
    <div className="h-full px-7">
      <Header
        content={<div className="p1-bold">{playlist?.PlaylistName}</div>}
        rightContent={
          <Dropdown
            options={[
              {
                onClick: () => {
                  deletePlaylist.mutate(
                    {
                      PlaylistId: playlist.PlaylistId,
                    },
                    {
                      onSuccess: () => {
                        toast(
                          `${translate("Deleted")} ${
                            playlist?.PlaylistName || translate("Playlist")
                          } `,
                        );
                      },
                    },
                  );
                },
                text: translate("Delete Playlist"),
                icon: <Delete />,
                color: "error",
              },
            ]}
            contentProps={{ align: "end" }}
          >
            <Ellipsis className="h-6 w-6" />
          </Dropdown>
        }
      />
      {contentNotLoaded || noItems ? (
        <div className="flex h-4/5 w-full  items-center justify-center">
          <div className="flex w-[200px] flex-col items-center text-center">
            <img
              className={cn(isDarkMode && "invert")}
              height={68}
              width={68}
              alt="playlist"
              src={getTabIconFilename({ icon: "icon_playlist.png" })}
            />
            <div className="h4-bold  mt-4">
              {translate("No playlist items")}
            </div>
            <div className="p1 text-item-secondary mt-2">
              {translate("Looks like you haven't saved anything yet")}
            </div>
          </div>
        </div>
      ) : (
        <PlaylistGrid
          fullItems={{ videos: videoById, collections: collectionById }}
          items={items}
        />
      )}
    </div>
  );
};
